import React, { useRef, useState, useEffect } from 'react'
import { useStores } from 'hooks/useStores'
import PlaceIcon from '@material-ui/icons/Place'
import LottieComponent from 'themes/LottieComponent'
import GooglePlacesAutoCompleteNew from 'components/common/GooglePlacesAutoCompleteNew'
import { observer } from 'mobx-react'
import ButtonBase from 'components/common/ButtonBase'
import {
	Container,
	CurrentLocationIcon,
	CurrentLocationIconContainer,
	StyledAddressSelectorBody,
	StyledAddressSelectorContainer,
	StyledBackIcon,
	StyledButtonsContainer,
	StyledEditAddressBody,
	StyledEditAddressContainer,
	StyledHeaderTypography,
	StyledHeartIcon,
	StyledInput,
	StyledLocationOnMap,
	StyledPlaceTypography,
	StyledWrapper,
} from './AddressEntry.styles'
import { StepHeader, StyledCloseIcon } from 'components/localization/style'
import type { AddressLabel, NonSavedAddress } from 'mobx/Localization/types'
import type _Home from 'mobx/Home'
import type _Infra from 'mobx/Infra'
import type _MobileApplication from 'mobx/MobileApplication'
import AddressSavingSection from './AddressSavingSection'
import WithGuard from 'HOCs/withGuard'
import { Guard } from 'mobx/Infra/Infra.type'
import { getTranslatedTextByKey } from 'utils/language'
import { useFocusOnMobile } from 'hooks/useFocusOnMobile'
import { requestMyLocation, getGeoLocationPermissionState } from 'utils/utils'
import { geoCode } from 'utils/googleMapsUtils/googleMapsUtils'
import { useLocationMobileApp } from 'hooks/useLocationMobileApp'
import { CONSTANTS } from 'utils/constants'
import { injectStores } from 'mobx/injection'
import { EVENTS } from 'mobx/Localization/constants'

interface AddressEntryProps {
	MobileApplication: typeof _MobileApplication
	Home: typeof _Home
	Infra: typeof _Infra
	goToSavedAddresses: () => void
	goBack: () => void
}

const AddressEntry = ({ Home, Infra, MobileApplication, goToSavedAddresses, goBack }: AddressEntryProps) => {
	const { locale } = Home
	const { localizationStore } = useStores()
	const [currentAddress, setCurrentAddress] = useState(localizationStore.processingFlow?.address?.formatted || '')
	const [errorMessage, setErrorMessage] = useState('')
	const [buttonDisabled, setButtonDisabled] = useState(!localizationStore.processingFlow?.address)
	const inputRef = useRef<HTMLInputElement | null>(null)
	const addressTyped = useRef(false)

	useFocusOnMobile(inputRef, true)

	const toggleSavingCurrentAddress = () => {
		if (localizationStore.processingFlow?.savingAddressData) {
			localizationStore.processingFlow?.stopSavingAddress()
		} else {
			localizationStore.processingFlow?.startSavingAddress()
		}
	}

	useEffect(() => {
		const init = async () => {

			if (Infra?.appParams?.features?.blockAutoLocator) {
				return
			}

			const navigatorPermissionState = await getGeoLocationPermissionState()
			const showError =
				navigatorPermissionState !== 'denied' &&
				MobileApplication.userLocationPermissionStatus !== CONSTANTS.MOBILE_APP.PERMISSIONS.LOCATION.DENIED
			requestMyLocation(onLocationPermissionGranted, showError)
		}

		init()
	}, [])

	const onChangeAddress = (e: React.ChangeEvent<HTMLInputElement>) => {
		setCurrentAddress(e.target.value)
		setButtonDisabled(true)
		setErrorMessage('')

		if(addressTyped.current === false) {
			localizationStore.sendLocalizationEvent(EVENTS.TYPED_ADDRESS_MANUALLY)
			addressTyped.current = true
		}
	}

	const googlePlacesCallback = async(googlePlaceData: any = {}) => {
		const formatted = googlePlaceData.formatted || googlePlaceData.formatted_address
		const lat = googlePlaceData.lat || googlePlaceData.geometry?.location.lat()
		const lng = googlePlaceData.lng || googlePlaceData.geometry?.location.lng()
		const components = googlePlaceData.address_components || []

		const address: NonSavedAddress = {
			formatted,
			location: { lat, lng },
			components,
		}

		if (formatted) {
			localizationStore.processingFlow?.setAddress(address)

			setCurrentAddress(formatted)
			setErrorMessage('')
			setButtonDisabled(false)
		}
	}

	const handleContinue = async () => {
		if (!localizationStore.processingFlow?.address) {
			setErrorMessage(getTranslatedTextByKey('webviewFlow.localization.selectAddressFromSelection', 'Please select address from the list'))
			return
		}

		Infra.setLoading(true)
		await localizationStore.matchAndSetStoreForAddress(localizationStore.processingFlow.address)
		Infra.setLoading(false)

		localizationStore.nextStep()
	}

	const onLocationPermissionGranted = async (location: { lat: number; long: number }) => {
		const geoCodeResult = await geoCode(
			{
				location: {
					lat: location.lat,
					lng: location.long,
				},
			},
			locale.key
		)
		googlePlacesCallback(geoCodeResult)
	}

	const onChangeLabel = (label: AddressLabel) => {
		localizationStore.processingFlow?.setSavingAddressLabel(label)
		localizationStore.sendLocalizationEvent(EVENTS.LOCATION_TYPE, {
			Label: label
		})
	}

	const onChangeCustomLabel = (customLabel: string) => {
		if (customLabel.length <= 25) {
			localizationStore.processingFlow?.setSavingAddressCustomLabel(customLabel)
		}
	}

	const onSetLocationOnMapClick = () => {
		// reset the store in case of a store was already set (from editing or matchAndSetStoreForAddress)
		localizationStore.processingFlow?.resetStore()
		localizationStore.nextStep()

		localizationStore.sendLocalizationEvent(EVENTS.SHARED_LOCATION_ON_MAP)
	}

	const onCurrentLocationIconClick = () => {
		requestMyLocation(onLocationPermissionGranted)
		localizationStore.sendLocalizationEvent(EVENTS.CLICKED_LOCATION_ICON)
	}

	useLocationMobileApp(onLocationPermissionGranted)

	const signupTitle = getTranslatedTextByKey('webviewFlow.localization.signupToSaveAddress', 'Sign up to access and save your addresses')

	return (
		<Container>
			<StyledAddressSelectorContainer>
				<StepHeader>
					<StyledBackIcon data-testid="back-button" onClick={goBack} />
					<StyledHeaderTypography variant="SubheadHighlighted">
						{getTranslatedTextByKey('webviewFlow.localization.enterAddress', 'Enter your address')}
					</StyledHeaderTypography>
					<StyledCloseIcon data-testid="close-button" onClick={localizationStore.abort} />
				</StepHeader>
				<StyledAddressSelectorBody>
					<StyledEditAddressContainer>
						<StyledEditAddressBody>
							<StyledWrapper>
								<StyledHeaderTypography variant="BodySmallHighlighted">
									{getTranslatedTextByKey('webviewFlow.localization.address', 'Address')}
								</StyledHeaderTypography>
							</StyledWrapper>
							<StyledWrapper>
								<GooglePlacesAutoCompleteNew
									{...locale}
									apiKey={locale.key}
									onPlaceSelected={googlePlacesCallback}
									onGooglePlacesReady={() => {
										inputRef.current?.focus()
									}}
									renderTextField={(ref: any) => (
										<StyledInput
											inputRef={(node) => {
												ref.current = node
												inputRef.current = node
											}}
											id="localization-address-selection-page-type-location"
											value={currentAddress}
											onChange={onChangeAddress}
											error={errorMessage !== ''}
											errorMessage={errorMessage}
											onResetValue={() => {
												setCurrentAddress('')
												setErrorMessage('')
												localizationStore.processingFlow?.clearAddress()
											}}
											inputProps={{ 'data-testid': 'map-dialog-input' }}
											endAdornment={
												<CurrentLocationIconContainer 
													title={getTranslatedTextByKey('webviewFlow.localization.useYourLocation', 'Use your current location')} 
													data-testid="current-location-container" 
													onClick={onCurrentLocationIconClick}>
													<CurrentLocationIcon />
												</CurrentLocationIconContainer>
											}
										/>
									)}
								/>
							</StyledWrapper>
						</StyledEditAddressBody>
						{localizationStore.savedAddressesEnabled && (
							<AddressSavingSection
								savingAddress={!!localizationStore.processingFlow?.savingAddressData}
								label={localizationStore.processingFlow?.savingAddressData?.label}
								customLabel={localizationStore.processingFlow?.savingAddressData?.customLabel}
								onToggleSavingAddress={toggleSavingCurrentAddress}
								onChangeLabel={onChangeLabel}
								onChangeCustomLabel={onChangeCustomLabel}
								disabled={localizationStore.reachedMaximumSavedAddresses}
							/>
						)}
						<StyledButtonsContainer>
							<ButtonBase data-testid="continue-button" onClick={handleContinue} disabled={buttonDisabled}>
								{getTranslatedTextByKey('webviewFlow.localization.continue', 'Continue')}
							</ButtonBase>
							{localizationStore.savedAddressesEnabled && (
								<WithGuard
									context={Guard.Context.always}
									type={Guard.Type.event}
									name={Guard.Name.savedAddresses}
									overrideSignupParams={{ signupTitle, signupDisplaySkipSection: false }}
								>
									<ButtonBase data-testid="saved-addresses-button" onClick={goToSavedAddresses} $type="secondary">
										<StyledHeartIcon />
										{getTranslatedTextByKey('webviewFlow.localization.savedAddresses', 'Saved Addresses')}
									</ButtonBase>
								</WithGuard>
							)}
							<StyledLocationOnMap data-testid="set-location-on-map-button" onClick={onSetLocationOnMapClick}>
								<StyledPlaceTypography variant="CTATextSmall">
									<PlaceIcon width="16px" height="16px" />
									{getTranslatedTextByKey('webviewFlow.localization.setLocationOnMap', 'Set location on map')}
								</StyledPlaceTypography>
							</StyledLocationOnMap>
						</StyledButtonsContainer>
					</StyledEditAddressContainer>
				</StyledAddressSelectorBody>
			</StyledAddressSelectorContainer>
			<LottieComponent fileName="localization" filePerDevice alt="localization" width="512px" />
		</Container>
	)
}

export default injectStores('Home', 'Infra', 'MobileApplication')(observer(AddressEntry))
